import { KeyRound, Pencil, Star } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { ClientFormDialog } from '@/pages/admin/clients/ClientFormDialog';
import { ClientInvoicesTab } from '@/pages/admin/clients/ClientInvoicesTab';
import { ClientNotesTab } from '@/pages/admin/clients/ClientNotesTab';
import { ClientPreferencesTab } from '@/pages/admin/clients/ClientPreferencesTab';
import { ClientPropertiesTab } from '@/pages/admin/clients/ClientPropertiesTab';
import { PortalLoginCard } from '@/pages/admin/clients/PortalLoginCard';
import { CorrespondencePanel } from '@/pages/admin/conversations/CorrespondencePanel';
import { CustomerTimeline } from '@/pages/admin/clients/CustomerTimeline';
import { useClient } from '@/hooks/clients';
import { formatDay } from '@/lib/dates';
import { cn } from '@/lib/utils';
import { formatCents } from '@shared/money';
import type { ClientDetail } from '@shared/schemas/client';

/*
 * /admin/clients/:id — ROADMAP.md §3.6.
 *
 * The tabbed profile from the prototype. Two of its six tabs were deliberately
 * absent rather than empty, because a tab that opens onto "nothing here yet"
 * for two phases is a tab people learn to skip. They arrive with the feature:
 *
 *   - **Service History** needs jobs, which are phase 4.
 *   - ~~**Messages** needs the lead thread, which is phase 9.~~ **Arrived as
 *     `Conversations`, ROADMAP §9.7.** The name changed with the model: it is
 *     not a lead thread, it is one conversation model serving leads, clients and
 *     strangers, so a customer's history survives the moment somebody clicked
 *     Convert. `lead_message` — the lead-only table this tab was originally
 *     waiting on — is struck in data-model.md §7 and was never built.
 *
 * **This page is where P10 is fixed**, and the fix is partly a removal — see
 * the Overview tab.
 */

const TABS = [
  'Overview',
  'Timeline',
  'Properties',
  'Invoices',
  'Conversations',
  'Notes',
  'Preferences',
] as const;
type Tab = (typeof TABS)[number];

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const client = useClient(id);
  const [tab, setTab] = useState<Tab>('Overview');
  const [editing, setEditing] = useState(false);

  if (client.isError) {
    return (
      <AdminPage title="Client">
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="That client could not be loaded"
            onRetry={() => {
              void client.refetch();
            }}
          />
        </div>
      </AdminPage>
    );
  }

  if (client.isPending) {
    return (
      <AdminPage title="Client">
        <div aria-busy="true" className="space-y-3">
          <span className="sr-only">Loading the client</span>
          <div aria-hidden="true" className="h-32 animate-pulse rounded-lg bg-white" />
          <div aria-hidden="true" className="h-64 animate-pulse rounded-lg bg-white" />
        </div>
      </AdminPage>
    );
  }

  const detail = client.data;

  return (
    <AdminPage
      title={detail.displayName}
      record={detail.displayName}
      description={detail.businessName ? `Contact: ${detail.contactName}` : undefined}
      actions={
        <Button
          variant="outline"
          onClick={() => {
            setEditing(true);
          }}
        >
          <Pencil aria-hidden="true" />
          Edit client
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {detail.isHighPriority ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-honey-50 px-2.5 py-0.5 text-meta font-medium text-bark-900">
            <Star aria-hidden="true" className="size-3.5 fill-honey-400 text-bark-900" />
            High priority
          </span>
        ) : null}
        {detail.isActive ? null : <StatusBadge label="Archived" tone="cancelled" />}
      </div>

      <nav aria-label="Client sections" className="mb-4 flex flex-wrap gap-2" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry}
            type="button"
            role="tab"
            aria-selected={tab === entry}
            onClick={() => {
              setTab(entry);
            }}
            className={cn(
              'inline-flex min-h-touch items-center rounded-full px-3.5 text-label',
              tab === entry
                ? 'bg-honey-400 text-ink-900'
                : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
            )}
          >
            {entry}
            {entry === 'Properties' ? (
              <span className="ml-1.5 rounded-full bg-ink-900/10 px-1.5 text-meta">
                {detail.propertyCount}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      {tab === 'Overview' ? <Overview detail={detail} /> : null}
      {/*
        **A read across the existing tables** — the enquiry, the messages, the
        jobs — rather than a table of its own that every write path would have
        to remember to update (ROADMAP §9.7).
      */}
      {tab === 'Timeline' ? <CustomerTimeline clientId={detail.id} /> : null}
      {tab === 'Properties' ? <ClientPropertiesTab detail={detail} /> : null}
      {/*
        **The invoices seam, closed by the audit of 2026-08-06.** Phase 12 built
        the client-scoped list query and a New-invoice dialog that takes a
        preset client, and nothing ever called either — so the office could see
        every invoice except from the screen it would look on.
      */}
      {tab === 'Invoices' ? (
        <ClientInvoicesTab clientId={detail.id} clientName={detail.displayName} />
      ) : null}
      {/*
        **Distinct from Notes, and the difference matters.** A `client_note` is
        a fact about the customer; an internal note inside a conversation is a
        remark in that conversation, beside the message it is about
        (business-rules.md §10). Two tabs, because they answer different
        questions.
      */}
      {tab === 'Conversations' ? (
        <CorrespondencePanel
          clientId={detail.id}
          // What makes the "Start a conversation" button appear — the owner's
          // 2026-08-07 finding was that nothing anywhere could open a thread.
          contactName={detail.displayName}
          contactEmail={detail.email}
        />
      ) : null}
      {tab === 'Notes' ? <ClientNotesTab detail={detail} /> : null}
      {tab === 'Preferences' ? <ClientPreferencesTab detail={detail} /> : null}

      <ClientFormDialog open={editing} onOpenChange={setEditing} client={detail} />
    </AdminPage>
  );
}

function Overview({ detail }: { detail: ClientDetail }) {
  /*
   * **P10, and the fix is partly a removal.**
   *
   * The prototype shows three metrics on this screen. Two of them have nothing
   * behind them: "Customer since —" is blank because nothing ever writes the
   * column, and "Lifetime Value" is a number with no invoices anywhere in the
   * system to add up.
   *
   *   - `customerSince` now has a real value from the moment a client is
   *     created, so it is shown.
   *   - `satisfactionRating` is a real field an admin sets, so it is shown — and
   *     labelled internal, because open question 8 says it is (never shown to
   *     the client).
   *   - **Lifetime value is here at last, and it is two numbers.** The audit of
   *     2026-08-06 put the question to the owner rather than picking a
   *     definition, because "what is this customer worth" has two defensible
   *     answers — what they have been *billed* and what they have actually
   *     *paid*. The answer was: collected is the headline, because it is the
   *     figure that reconciles against the bank, with billed beside it so the
   *     difference is visible. That difference is what is still owed.
   */
  return (
    <div className="space-y-4">
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="Customer since">
          {detail.customerSince ? (
            formatDay(detail.customerSince)
          ) : (
            <span className="text-ink-500">Not recorded</span>
          )}
        </Fact>

        <Fact label="Satisfaction" hint="Internal — the client never sees this.">
          {detail.satisfactionRating === null ? (
            <span className="text-ink-500">Not yet rated</span>
          ) : (
            <span className="flex items-center gap-1">
              {Array.from({ length: 5 }, (_, index) => (
                <Star
                  key={index}
                  aria-hidden="true"
                  className={cn(
                    'size-4',
                    index < detail.satisfactionRating!
                      ? 'fill-honey-400 text-bark-700'
                      : 'text-ink-300',
                  )}
                />
              ))}
              <span className="sr-only">{detail.satisfactionRating} out of 5</span>
            </span>
          )}
        </Fact>

        <Fact label="Properties">{detail.propertyCount}</Fact>

        {/*
          **"Never" is the answer, not a dash** (P1, P10). A customer the office
          has only ever cleaned for has genuinely never been in touch, and that
          is worth knowing rather than hiding behind an em-dash.

          An internal note does not count — nobody spoke to anybody. A logged
          phone call does, which is the whole reason phone logs are a separate
          direction and not a note whose body starts with "rang them".
        */}
        <Fact label="Last contact" hint="A message either way, or a call somebody wrote down.">
          {detail.lastContact === null ? (
            <span className="text-ink-500">Never</span>
          ) : (
            <>
              {formatDay(detail.lastContact.at.slice(0, 10))}
              <span className="mt-0.5 block text-meta font-normal text-ink-500">
                {detail.lastContact.description}
              </span>
            </>
          )}
        </Fact>

        {/*
          **Collected is the headline and billed is the context.** A customer
          who has been invoiced $1,000 and paid $200 is not a $1,000 customer,
          and one number alone cannot say which of the two it is. Zero is
          `$0.00` (P1, P10) — a customer who has never been billed is a fact.
        */}
        <Fact
          label="Lifetime value"
          hint="What they have paid. Invoiced is what has gone out — the gap is what is owed."
        >
          <span className="tabular-nums">{formatCents(detail.lifetime.collectedCents)}</span>
          <span className="mt-0.5 block text-meta font-normal text-ink-500">
            {formatCents(detail.lifetime.billedCents)} invoiced
          </span>
        </Fact>

        <Fact label="Email">
          {detail.email ?? <span className="text-ink-500">Not recorded</span>}
        </Fact>
        <Fact label="Phone">
          {detail.phone ?? <span className="text-ink-500">Not recorded</span>}
        </Fact>
      </dl>

      {/* The client portal's front door — ROADMAP.md §5.1. A card rather than a
          Fact, because unlike the rest of this grid it is something to do. */}
      <PortalLoginCard detail={detail} />

      {/*
        **This sentence has now been wrong twice, which is why it is short.** It
        spent two phases promising service history "with jobs" (phase 4) and
        lifetime value "with invoicing" (phase 12) after both had shipped; then
        the audit rewrote it to say there was no lifetime-value figure, and the
        owner answered the question hours later. It now points at the tabs and
        makes no claim that can expire.
      */}
      <p className="text-meta text-ink-500">
        Every clean is on Timeline, and every invoice is on Invoices.
      </p>

      {detail.properties.some((property) => property.accessNotes) ? (
        <p className="flex items-start gap-2 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
          <KeyRound aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          Some of this client&rsquo;s properties have access notes — lockbox or gate codes. They are
          on the Properties tab, and a cleaner only ever sees them for a job they are assigned to.
        </p>
      ) : null}
    </div>
  );
}

function Fact({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <dt className="text-meta text-ink-500">{label}</dt>
      <dd className="mt-1 text-h3 text-ink-900">{children}</dd>
      {hint ? <p className="mt-1 text-meta text-ink-500">{hint}</p> : null}
    </div>
  );
}
